import { EyeDropperIcon } from '#icons';
import { Alert, Box, IconButton, InputAdornment, Popover, Stack, TextField, decomposeColor } from '@mui/material';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import { useTranslation } from '@modernloop/i18n';
import { FC, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';

// EyeDropper API type (experimental browser API)
interface EyeDropperResult {
  sRGBHex: string;
}

interface EyeDropperConstructor {
  new (): {
    open: () => Promise<EyeDropperResult>;
  };
}

const DEFAULT_COLOR = ['#1160D4', '#B146BE', '#0E8E8A', '#E27121'];

type Props = {
  color: string;
  onChange: (color: string) => void;
  customErrorMessage?: string;
};

const Picker = styled(HexColorPicker)`
  .react-colorful__hue-pointer,
  .react-colorful__pointer {
    width: 14px;
    height: 14px;
    border-radius: '50%';
  }
  .react-colorful__hue {
    height: 12px;
    border-radius: 8px;
    margin: 10px 0px;
  }
  .react-colorful__saturation {
    border-radius: 8px;
  }
`;

const ColorPicker: FC<Props> = ({ color, onChange, customErrorMessage }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorEl = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState(customErrorMessage);

  const handleChange = (color: string) => {
    validateColor(color);
    onChange(color);
  };
  // SAFETY: The optional browser EyeDropper API has the constructor and open method declared above.
  const EyeDropper = get(window, 'EyeDropper') as EyeDropperConstructor | undefined;

  const handleEyeDropperClick = () => {
    if (!EyeDropper) {
      return;
    }

    const eyeDropper = new EyeDropper();

    eyeDropper
      .open()
      // eslint-disable-next-line promise/always-return
      .then((result: EyeDropperResult) => {
        onChange(result.sRGBHex);
      })
      .catch(() => {
        console.log('EyeDropper is not supported');
      });
  };

  const validateColor = (color: string) => {
    setErrorMessage('');
    try {
      decomposeColor(color);
      if (!CSS.supports('color', color)) {
        throw new Error('Invalid color');
      }
    } catch {
      setErrorMessage(t('common:shared.colorPicker.invalidColor'));
    }
  };

  return (
    <Stack spacing={1}>
      <TextField
        type="text"
        variant="outlined"
        onChange={(e) => {
          handleChange(e.target.value);
        }}
        size="medium"
        placeholder={t('common:shared.colorPicker.selectColor')}
        value={color}
        inputRef={anchorEl}
        inputProps={{
          onClick: () => setOpen(!open),
        }}
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box width={20} height={20} bgcolor={color} borderRadius="4px" />
            </InputAdornment>
          ),
        }}
        error={Boolean(errorMessage)}
      />
      {errorMessage && (
        <Alert
          severity="error"
          // eslint-disable-next-line no-restricted-syntax
          sx={{ whiteSpace: 'pre-line' }}
        >
          {errorMessage}
        </Alert>
      )}

      <Popover
        anchorEl={anchorEl.current}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box display="flex" justifyContent="center" padding={2}>
          <Stack spacing={0.5}>
            <Picker color={color} onChange={handleChange} />
            <Stack direction="row" justifyContent="space-between">
              {DEFAULT_COLOR.map((color) => (
                <Box
                  key={color}
                  width={30}
                  height={30}
                  borderRadius={15}
                  bgcolor={color}
                  onClick={() => {
                    handleChange(color);
                    setOpen(false);
                  }}
                />
              ))}
              {EyeDropper && (
                <IconButton onClick={handleEyeDropperClick}>
                  <EyeDropperIcon />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </Stack>
  );
};

export default ColorPicker;
